import { ErrorHandler, Injector, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorMessage } from '../public/store/+state/stores.actions';
import { WindowRefService } from '../window.service';
import {
  AdminDiagnosticContext,
  GlobalErrorService,
} from './global-error.service';
import { LogService } from '../shared/logging/LogService';
import { WhitelabelService } from '../shared/whitelabel.service';
import { isDynamicImportFailure } from './chunk-load-error.util';
import {
  resolveIncidentReference,
  UNEXPECTED_ERROR_CODE,
} from './incident-reference.util';

const CHUNK_RELOAD_STORAGE_KEY = 'admin-error:chunkReloadAt';
const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  constructor(
    private injector: Injector,
    private whitelabelService: WhitelabelService,
  ) {}

  handleError(error) {
    // 401 must run before the chunk-load branch so an auth failure wrapped in
    // a chunk-style message still goes through the auth reload path.
    const unauthorizedMessage = /401 Unauthorized/i;
    if (unauthorizedMessage.test(error?.message)) {
      this.performReload();
      return;
    }

    if (isDynamicImportFailure(error)) {
      if (this.tryChunkReload()) {
        return;
      }
      // Cooldown active — fall through to the diagnostic page so the user
      // can read the failure details instead of looping.
    }

    const geoCodeNoResultMessage =
      /MapsRequestError: GEOCODER_GEOCODE: ZERO_RESULTS:/;
    if (geoCodeNoResultMessage.test(error?.message)) {
      return;
    }

    // "NG0100: ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked.
    // Return to not create a loop of errors
    const ng0100Message = /NG0100/;
    if (ng0100Message.test(error?.message)) {
      return;
    }

    const errorService = this.injector.get(GlobalErrorService);
    const windowRefSer = this.injector.get(WindowRefService);
    const logService = this.injector.get(LogService);

    if (
      windowRefSer.nativeWindowLocation.hostname.startsWith('admin') ||
      this.whitelabelService.isWhiteLabelDomainByHostName(
        windowRefSer.nativeWindowLocation.hostname,
      )
    ) {
      // Admin side
      logService.error('error', error?.stack || error);
      errorService.currentAngularError = error?.stack || error;
      errorService.setDiagnosticContext(
        this.buildAdminDiagnosticContext(error),
      );
      // For all unhandled errors in admin side go to error page
      errorService.navigateToErrorPage(['/manager/expectederror']);
    } else {
      // Public side — build reference, log sanitized context, dispatch error action
      const reference = resolveIncidentReference(error);
      const ctx = this.buildPublicDiagnosticContext(error, reference);
      logService.error('customer ui error', ctx);
      errorService.dispatchErrorAction(
        new ErrorMessage(
          'public.global.errorUnexpected',
          UNEXPECTED_ERROR_CODE,
          [],
          undefined,
          undefined,
          reference,
        ),
      );
    }
  }

  // Returns true if we performed the reload, false if the cooldown blocked it.
  private tryChunkReload(): boolean {
    try {
      const now = Date.now();
      const raw = window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
      const lastAt = raw ? Number(raw) : 0;
      if (
        Number.isFinite(lastAt) &&
        lastAt > 0 &&
        now - lastAt < CHUNK_RELOAD_COOLDOWN_MS
      ) {
        return false;
      }
      window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
    } catch {
      // sessionStorage unavailable (e.g. private mode) — still reload; the
      // alternative is leaving the user on an unrecoverable stale chunk.
    }
    this.performReload();
    return true;
  }

  // Indirected for testability — Karma cannot spy on window.location.reload
  // on every browser configuration; spying on this method works everywhere.
  protected performReload(): void {
    window.location.reload();
  }

  private buildPublicDiagnosticContext(error: any, reference: string): object {
    const router = this.injector.get(Router);
    let route: string | undefined;
    try {
      route = router?.url;
    } catch {
      route = undefined;
    }

    return {
      reference,
      url: typeof error?.url === 'string' ? error.url : undefined,
      status: typeof error?.status === 'number' ? error.status : undefined,
      name: typeof error?.name === 'string' ? error.name : undefined,
      message: typeof error?.message === 'string' ? error.message : undefined,
      route,
      timestamp: new Date().toISOString(),
    };
  }

  private buildAdminDiagnosticContext(error: any): AdminDiagnosticContext {
    const router = this.injector.get(Router);
    let route: string | undefined;
    try {
      route = router?.url;
    } catch {
      route = undefined;
    }

    return {
      url: typeof error?.url === 'string' ? error.url : undefined,
      status: typeof error?.status === 'number' ? error.status : undefined,
      name: typeof error?.name === 'string' ? error.name : undefined,
      message: typeof error?.message === 'string' ? error.message : undefined,
      stack: typeof error?.stack === 'string' ? error.stack : undefined,
      route,
      at: new Date().toISOString(),
    };
  }
}
